import type { ClassStudent, StudentUser } from "@/types/canonical";
import type { AssignmentSectionProps } from "../config";

// Поля для поиска у индивидуального студента есть, только если его собрал mapExternalStudents.
type StudentUserWithGroup = StudentUser &
  Partial<Pick<ClassStudent, "patronymic" | "username" | "email">> & { group?: string | null };

type Participant = StudentUserWithGroup | ClassStudent;

type AssignmentLike = AssignmentSectionProps["data"] | undefined;

/**
 * Участники обязательны только при свободном доступе: в режимах `private` (инвайт-код)
 * и `prepaid` (покупка) студенты подключаются к заданию самостоятельно.
 */
export const isParticipantsRequired = (data: AssignmentLike) =>
  (data?.access_type ?? "free") === "free";

/** Выбрана ли группа или добавлены ли студенты индивидуально. */
export const hasSelectedParticipants = (data: AssignmentLike) => {
  const students = (data as any)?.students;

  return (
    typeof data?.class_id === "number" ||
    (Array.isArray(students) && students.length > 0)
  );
};

/*
 * На бэкенде у задания одна группа (class_id). Группы, добавленные к ней сверху,
 * фронт помнит в settings задания — иначе в «Студентах» не видно, откуда пришли люди.
 * Ключ сохраняется вместе с заданием по «Сохранить».
 */
export const ADDED_GROUPS_KEY = "participant_groups";

export type AddedGroup = { id: number; name: string };

/** Блок «Студентов»: добавленная группа или остальные (`id: null`). */
export type StudentsBlock = { id: number | null; name: string | null; students: StudentUser[] };

export const getAddedGroups = (data: AssignmentLike): AddedGroup[] => {
  const groups = (data as { settings?: Record<string, unknown> } | undefined)?.settings?.[ADDED_GROUPS_KEY];
  return Array.isArray(groups)
    ? groups.filter((group): group is AddedGroup => typeof (group as AddedGroup)?.id === "number")
    : [];
};

export const getGroupInitials = (name?: string | null) => {
  if (!name) return "";
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    ?.toUpperCase();
};

export const getParticipantFullName = (student: Participant) => {
  const fullName = `${student.firstname ?? ""} ${student.lastname ?? ""}`.trim();
  return fullName || "";
};

/**
 * Участники под строку поиска: каждое слово запроса должно найтись в Ф.И.О., логине
 * (ИИН) или почте, порядок слов не важен — «Иванов Иван» и «Иван Иванов» равны.
 */
export const filterParticipants = <T extends Participant>(students: T[], query: string): T[] => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return students;

  return students.filter((student) => {
    const haystack = [
      student.lastname,
      student.firstname,
      student.patronymic,
      student.username,
      student.email,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return words.every((word) => haystack.includes(word));
  });
};

export const getParticipantInitials = (student: Participant) => {
  const parts = [student.firstname, student.lastname].filter(
    (value): value is string => Boolean(value)
  );

  if (parts.length === 0) {
    return "??";
  }

  return parts
    .map((value) => value[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
};

export const getParticipantAvatar = (student: Participant) => {
  return (
    (student as any)?.photo_thumb?.medium ||
    (student as any)?.photo ||
    ""
  );
};

/**
 * Returns the translation key for the participant's role.
 * Use with t() function to get the translated label.
 */
export const getParticipantRoleLabel = (student: Participant): string => {
  const group = (student as StudentUserWithGroup).group ?? "student";

  switch (group) {
    case "student":
      return "label-student";
    case "teacher":
      return "label-teacher";
    case "manager":
      return "label-manager-role";
    case "proctor":
      return "label-proctor-role";
    case "reviewer":
      return "label-reviewer";
    case "admin":
      return "label-manager-role";
    default:
      return group;
  }
};
